import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectCurrentUser, setCurrentUser } from "../Users/usersSlice";
import { EULA } from "./EULA";
import { completeOnboarding } from "./inviteUtils";
import { fetchUsersByEmail } from "../Users/usersAPI";
import { User } from "../../API";
import { useAuthenticator } from "@aws-amplify/ui-react";

/**
 * EulaGate component that blocks app access until EULA is accepted.
 * Shows EULA modal if user hasn't accepted EULA yet.
 * Calls complete-onboarding API on acceptance, which handles both
 * EULA acceptance and auto-assignment of assessments.
 */
export const EulaGate = ({ children }: { children: React.ReactNode }) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const { user: amplifyUser, signOut } = useAuthenticator((context) => [context.user]);
  
  const [showEula, setShowEula] = useState(false);
  const [isAcceptingEula, setIsAcceptingEula] = useState(false);
  const [eulaError, setEulaError] = useState<string | null>(null);
  const [isRefreshingUser, setIsRefreshingUser] = useState(false);

  // Check if EULA needs to be shown
  useEffect(() => {
    if (currentUser && !currentUser.eulaAcceptedDate) {
      setShowEula(true);
    } else if (currentUser && currentUser.eulaAcceptedDate) {
      setShowEula(false);
    }
  }, [currentUser]);

  const handleEulaAccept = async () => {
    try {
      setEulaError(null);
      setIsAcceptingEula(true);

      // Call complete-onboarding API which handles both EULA acceptance and auto-assignment
      await completeOnboarding();

      // Refresh user data to get updated eulaAcceptedDate
      setIsAcceptingEula(false);
      setIsRefreshingUser(true);
      
      try {
        const email = amplifyUser?.attributes?.email?.toLowerCase();
        if (email) {
          const users = await fetchUsersByEmail(email);
          if (users && users.length > 0) {
            const updatedUser = users[0];
            dispatch(setCurrentUser(updatedUser));
            setShowEula(false);
          } else {
            throw new Error("User not found after EULA acceptance");
          }
        } else {
          throw new Error("Unable to determine user email");
        }
      } catch (refreshError) {
        console.error("Failed to refresh user data:", refreshError);
        setEulaError(
          "EULA accepted, but unable to refresh user data. Please refresh the page."
        );
      } finally {
        setIsRefreshingUser(false);
      }
    } catch (error: any) {
      console.error("Error in handleEulaAccept:", error);
      setIsAcceptingEula(false);
      setIsRefreshingUser(false);
      
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Unable to record EULA acceptance. Please try again.";
      setEulaError(errorMessage);
    }
  };

  const handleEulaCancel = async () => {
    // Sign out the user if they cancel EULA acceptance
    await signOut();
  };

  // Don't render children if EULA needs to be accepted
  if (currentUser && !currentUser.eulaAcceptedDate) {
    return (
      <>
        <EULA
          open={showEula}
          setOpen={() => {}} // Prevent closing without accepting - EULA must be accepted to use app
          loading={isAcceptingEula || isRefreshingUser}
          onAccept={handleEulaAccept}
          allowCancel={true}
          errorMessage={eulaError}
          onCancel={handleEulaCancel}
        />
        {/* Block app access - show nothing while EULA modal is open */}
      </>
    );
  }

  // EULA is accepted, render children
  return <>{children}</>;
};
